<template>
  <span
    class="company-approval-badge-wrap"
    v-b-tooltip.hover="tooltipOptions"
    @click.stop
  >
    <b-dropdown
      v-if="canEdit"
      :variant="variant"
      size="sm"
      class="company-approval-badge"
      :class="`company-approval-badge--${normalizedStatus}`"
      no-caret
      right
      boundary="viewport"
      :disabled="busy"
      @show="onDropdownShow"
      @hidden="isDropdownOpen = false"
    >
      <template #button-content>
        <span class="status-label">{{ label }}</span>
        <b-icon-caret-down-fill class="ml-1" scale="0.7" />
      </template>

      <b-dropdown-item
        :disabled="busy || normalizedStatus === 'approved'"
        @click="onApproveClick"
      >
        Approve
      </b-dropdown-item>
      <b-dropdown-item @click="onRejectClick">
        {{
          normalizedStatus === 'rejected' ? 'Update rejection reason' : 'Reject'
        }}
      </b-dropdown-item>
      <b-dropdown-item
        :disabled="busy || normalizedStatus === 'pending'"
        @click="onPendingClick"
      >
        Pending
      </b-dropdown-item>
      <b-dropdown-item :disabled="busy" @click="onNotApplicableClick">
        {{ normalizedStatus === 'not_applicable' ? 'Unarchive' : 'Archive' }}
      </b-dropdown-item>
    </b-dropdown>

    <b-badge
      v-else
      pill
      :variant="variant"
      class="company-approval-badge company-approval-badge--readonly"
      :class="`company-approval-badge--${normalizedStatus}`"
    >
      <span class="status-label">{{ label }}</span>
    </b-badge>
  </span>
</template>

<script>
import { formatStatusSetDate } from '@/utils/fileAttention'

const STATUS_LABEL = {
  approved: 'Approved',
  pending: 'Pending',
  rejected: 'Rejected',
  not_applicable: 'Archived',
}

const STATUS_VARIANT = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
  not_applicable: 'secondary',
}

export default {
  name: 'CompanyApprovalBadge',

  data() {
    return {
      isDropdownOpen: false,
    }
  },

  props: {
    status: {
      type: String,
      default: 'pending',
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    reason: {
      type: String,
      default: '',
    },
    setDate: {
      type: String,
      default: '',
    },
  },

  computed: {
    normalizedStatus() {
      const s = (this.status || '').toLowerCase()
      return STATUS_LABEL[s] ? s : 'pending'
    },
    label() {
      return STATUS_LABEL[this.normalizedStatus]
    },
    variant() {
      return STATUS_VARIANT[this.normalizedStatus]
    },
    approvalTooltip() {
      const r = (this.reason || '').trim()
      if (this.normalizedStatus === 'rejected') {
        return r ? `Rejected: ${r}` : 'Rejected (no reason provided)'
      }
      if (this.normalizedStatus === 'approved') {
        return r ? `Approved: ${r}` : 'Approved'
      }
      if (this.normalizedStatus === 'pending') {
        return r ? `Pending: ${r}` : 'Pending'
      }
      if (this.normalizedStatus === 'not_applicable') {
        return r ? `Archived: ${r}` : 'Archived'
      }
      return ''
    },
    tooltipOptions() {
      const reasonPart = this.approvalTooltip
      if (!reasonPart) return { title: '', disabled: true }
      const dateStr = formatStatusSetDate(this.setDate)
      const title = dateStr ? `${reasonPart} - ${dateStr}` : reasonPart
      return {
        title,
        disabled: this.isDropdownOpen,
        customClass: 'company-approval-tooltip',
        // Explicit placement + a real boundary — left to auto-flip inside a
        // narrow table cell, Popper was treating the cell/row as its
        // available space, deciding there was no room in any direction that
        // didn't overlap the badge, and landing right on top of it instead
        // of actually clear of it.
        placement: 'top',
        boundary: 'window',
      }
    },
  },

  methods: {
    onApproveClick() {
      this.$emit('approve')
    },
    onRejectClick() {
      this.$emit('reject')
    },
    onPendingClick() {
      this.$emit('pending')
    },
    onNotApplicableClick() {
      if (this.normalizedStatus === 'not_applicable') {
        // Already archived — clicking "Unarchive" moves it back to pending
        this.$emit('pending')
      } else {
        this.$emit('not-applicable')
      }
    },
    onDropdownShow() {
      this.isDropdownOpen = true
      this.$root.$emit('bv::hide::tooltip')
    },
  },
}
</script>

<style lang="scss" scoped>
.company-approval-badge-wrap {
  display: inline-block;
}

.company-approval-badge {
  font-weight: 500;
  font-size: 0.72rem;
  letter-spacing: 0.01em;
  line-height: 1;
}

// When the dropdown is open inside a table, lift it above sibling rows so the
// menu (and its last item, e.g. "Unarchive") isn't painted over by a later row.
.company-approval-badge.show,
/deep/ .btn-group.show {
  position: relative;
  z-index: 1030;
}

// Ensure the menu itself stacks above adjacent table content.
/deep/ .dropdown-menu {
  z-index: 1031;
}

.company-approval-badge--readonly {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border: 1px solid transparent;
  border-radius: 999px;
  line-height: 1;
  background: transparent;
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 5px;
  background: currentColor;
  vertical-align: middle;
  flex-shrink: 0;
}

.status-label {
  vertical-align: middle;
}

/deep/ .btn-group {
  vertical-align: middle;
}

/deep/ .dropdown-toggle {
  font-weight: 500;
  font-size: 0.72rem;
  line-height: 1 !important;
  display: inline-flex;
  align-items: center;
  padding: 7px 12px !important;
  min-height: 0;
  height: auto;
  border-radius: 999px;
  border-width: 1px;
  box-shadow: none !important;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

/deep/ .dropdown-toggle .status-label,
/deep/ .dropdown-toggle .status-dot {
  line-height: 1;
}

/deep/ .dropdown-toggle::after {
  display: none;
}

/deep/ .dropdown-toggle .b-icon {
  opacity: 0.6;
  margin-left: 4px !important;
}

.company-approval-badge--approved /deep/ .dropdown-toggle,
.company-approval-badge--approved.company-approval-badge--readonly {
  background-color: #eaf6ee;
  color: #1f7a3a;
  border-color: #cfe9d7;
}
.company-approval-badge--approved /deep/ .dropdown-toggle:hover {
  background-color: #dcefe2;
  border-color: #b9dec4;
}

.company-approval-badge--pending /deep/ .dropdown-toggle,
.company-approval-badge--pending.company-approval-badge--readonly {
  background-color: #fdf6e3;
  color: #8a6a16;
  border-color: #f1e3b8;
}
.company-approval-badge--pending /deep/ .dropdown-toggle:hover {
  background-color: #f7ecc9;
  border-color: #e7d59b;
}

.company-approval-badge--rejected /deep/ .dropdown-toggle,
.company-approval-badge--rejected.company-approval-badge--readonly {
  background-color: #fdecee;
  color: #a3242e;
  border-color: #f3ccd1;
}
.company-approval-badge--rejected /deep/ .dropdown-toggle:hover {
  background-color: #f7d8dc;
  border-color: #ecb6bd;
}

.company-approval-badge--not_applicable /deep/ .dropdown-toggle,
.company-approval-badge--not_applicable.company-approval-badge--readonly {
  background-color: #eceef1;
  color: #495057;
  border-color: #d3d7dc;
}
.company-approval-badge--not_applicable /deep/ .dropdown-toggle:hover {
  background-color: #dde0e5;
  border-color: #c1c6cd;
}
</style>

<!-- Unscoped on purpose: BootstrapVue tooltips are teleported straight into
     <body>, outside this component's DOM subtree, so a scoped/deep selector
     here would never actually reach them — the data-v attribute scoping
     only applies to nodes that are still descendants at render time. The
     `.company-approval-tooltip` class name keeps this targeted to just this
     one tooltip rather than affecting every tooltip in the app. -->
<style lang="scss">
// The tooltip can render close enough to the dropdown toggle that moving the
// mouse toward it (to read a long reason) ends up hovering the tooltip
// itself instead of the button — from there, a click lands on inert tooltip
// markup rather than opening the dropdown. pointer-events: none makes the
// tooltip purely visual so the button underneath stays clickable regardless
// of exactly where Popper ends up placing it.
.company-approval-tooltip {
  pointer-events: none;
}
</style>
